import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Sun, Wrench, ShieldCheck, Zap, ChevronRight, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';

interface ServiceDemoProps {
  prospect: Prospect;
}

export const ServiceDemo: React.FC<ServiceDemoProps> = ({ prospect }) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-amber-500/30">
      {/* Header Industrial */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white border-b border-white/10 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center">
              <Sun className="text-slate-900 w-5 h-5" />
            </div>
            <h1 className="text-xl font-black tracking-tighter uppercase">{prospect.trade_name} Engenharia</h1>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-slate-300 uppercase tracking-wide">
            <a href="#solucoes" className="hover:text-amber-400 transition-colors">Soluções</a>
            <a href="#projetos" className="hover:text-amber-400 transition-colors">Projetos</a>
            <a href="#orcamento" className="hover:text-amber-400 transition-colors">Orçamento</a>
          </nav>
          <button className="bg-amber-500 text-slate-900 px-6 py-2.5 rounded font-bold text-sm hover:bg-amber-400 transition-all uppercase tracking-wide">
            Solicitar Cotação
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden bg-slate-900 flex items-center min-h-[85vh]">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&q=80&w=2000" 
            alt="Painéis Solares" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10 w-full">
          <div className="lg:w-3/5 space-y-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Zap className="w-4 h-4" />
              <span>Energia Solar & Climatização</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-white tracking-tighter leading-[1.1] uppercase">
              O futuro da <br />
              <span className="text-amber-500">
                eficiência.
              </span>
            </h2>
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed font-medium">
              Reduza até 95% da sua conta de energia. 
              Projetos personalizados de engenharia para residências e indústrias com a excelência {prospect.trade_name}.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
              <button className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-900 px-8 py-4 rounded font-black uppercase tracking-wider transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20">
                Fazer Orçamento Grátis
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                <ShieldCheck className="w-5 h-5 text-amber-500" />
                <span>25 anos de garantia de eficiência</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="py-24 px-6 bg-slate-50" id="solucoes">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal-on-scroll">
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-4 uppercase tracking-tight">Nossas Soluções</h2>
            <div className="w-20 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-slate-600 font-medium">Equipe qualificada NR-10 e NR-35 para garantir a máxima segurança na sua instalação.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 shadow-xl shadow-slate-200/50 p-8 rounded hover:-translate-y-2 transition-transform group reveal-on-scroll reveal-delay-100">
              <div className="w-16 h-16 bg-slate-900 rounded flex items-center justify-center mb-6 text-amber-500">
                <Sun className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3 uppercase">Energia Solar</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-600 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Homologação na concessionária
                </li>
                <li className="flex items-start gap-2 text-slate-600 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Manutenção preventiva
                </li>
              </ul>
            </Card>
            <Card className="bg-white border-0 shadow-xl shadow-slate-200/50 p-8 rounded hover:-translate-y-2 transition-transform group reveal-on-scroll reveal-delay-200">
              <div className="w-16 h-16 bg-slate-900 rounded flex items-center justify-center mb-6 text-amber-500">
                <Wrench className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3 uppercase">Climatização</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-600 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Instalação de Split e VRF
                </li>
                <li className="flex items-start gap-2 text-slate-600 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Higienização química
                </li>
              </ul>
            </Card>
            <Card className="bg-slate-900 border-0 shadow-xl shadow-slate-900/20 p-8 rounded hover:-translate-y-2 transition-transform group reveal-on-scroll reveal-delay-300 relative overflow-hidden">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-amber-500/20 rounded-full blur-xl"></div>
              <div className="w-16 h-16 bg-amber-500 rounded flex items-center justify-center mb-6 text-slate-900">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-white mb-3 uppercase">Elétrica Geral</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-300 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Padrão de entrada
                </li>
                <li className="flex items-start gap-2 text-slate-300 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" /> Automação residencial
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer Minimalista */}
      <footer className="bg-slate-950 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Sun className="text-amber-500 w-6 h-6" />
            <span className="text-white font-black text-lg uppercase tracking-tight">{prospect.trade_name} Engenharia</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400 font-medium">
            <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-amber-500" /> (49) 99117-2320</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-500" /> SMO - SC</span>
          </div>
          <div className="text-xs text-slate-600 font-medium">
            &copy; {new Date().getFullYear()} YouMachine. Demo Industrial.
          </div>
        </div>
      </footer>
    </div>
  );
};
